/**
 * Delivery-threshold metadata and pure normalization (#233).
 *
 * Splits cleanly in two:
 *
 * - `normalizeThreshold` / `normalizeDeliveryChannels` are the READ path. They
 *   are non-destructive: each channel is clamped against its OWN declared
 *   bounds, never against a sibling's value, and nothing here touches storage.
 * - `clampAgainstSibling` is the WRITE path, called from the setter on the one
 *   value being written. It is the only function that looks at a second
 *   channel, and it never returns an instruction to modify that sibling.
 *
 * The asymmetry is deliberate, and it is load-bearing: `setChannelSettings`
 * writes back whatever `readAlertPreferences` returned, so anything the read
 * path "fixes" is persisted the next time the user touches ANY unrelated
 * channel. Normalizing a pair on read would therefore rewrite one channel
 * because of another and destroy a value the user chose — inside a store whose
 * whole #231 design is losslessness.
 *
 * Storage keeping a reversed pair is NOT the same as delivering one. Ordering
 * is re-established where the pair is CONSUMED, by the two functions that read
 * it: `stepQueueDelivery` orders its bounds, and `QueueVelocityTracker
 * .setThresholds` orders its own. Both values stay stored, both stay editable,
 * and no consumer can observe an inverted pair. Editing either field also
 * repairs the stored pair, since the setter clamps.
 *
 * `registry.ts` imports `DeliveryThresholdMeta` from here TYPE-ONLY, so the
 * runtime dependency edge points one way (this module -> registry) and there is
 * no import cycle.
 */

import {
  ALERT_CHANNEL_IDS,
  ALERT_REGISTRY,
  type AlertChannelId,
  type AlertChannelSettings,
} from './registry';

export type DeliveryThresholdUnit = 'contracts' | 'contractsPerSecond' | 'cents';

/**
 * Real, renderable bounds — #232 builds its number input from these, so
 * `Number.MAX_SAFE_INTEGER` is not an acceptable `max`. `step` is the UI
 * increment AND the gap used when holding an ordered pair apart; it is NOT a
 * storage quantizer (a stored 12,345 stays 12,345).
 */
export interface DeliveryThresholdMeta {
  unit: DeliveryThresholdUnit;
  min: number;
  max: number;
  step: number;
}

/**
 * Ordered channel pairs. `lower` must stay strictly below `upper`.
 *
 * Queue thresholds count DOWN toward the fill (critical is nearer the front
 * than warning); velocity counts UP (warning is slower than critical). Strict
 * inequality matters: at equal thresholds every crossing is a critical
 * crossing, and both the sound priority and the card precedence hand the tick
 * to critical, so the warning tier has no band of its own left to own.
 */
const ORDERED_PAIRS: ReadonlyArray<{ lower: AlertChannelId; upper: AlertChannelId }> = [
  { lower: 'queueCritical', upper: 'queueWarning' },
  { lower: 'velocityWarning', upper: 'velocityCritical' },
];

/** Decimal places implied by a step: 1_000 -> 0, 0.5 -> 1, 0.1 -> 1. */
function decimalsOf(step: number): number {
  const s = String(step);
  const dot = s.indexOf('.');
  return dot === -1 ? 0 : s.length - dot - 1;
}

/**
 * Contract counts are integral (a fractional resting size is meaningless), so
 * they floor. Fractional units round to the precision their step implies,
 * which also scrubs float dust like 20 - 0.1 = 19.900000000000002.
 */
function quantize(value: number, meta: DeliveryThresholdMeta): number {
  if (meta.unit === 'contracts') return Math.floor(value);
  return Number(value.toFixed(decimalsOf(meta.step)));
}

function clampToBounds(value: number, meta: DeliveryThresholdMeta): number {
  return Math.max(meta.min, Math.min(meta.max, value));
}

/**
 * Clamp + quantize one value against its own channel's bounds. Anything not a
 * finite number falls back to the supplied default. Never touches siblings.
 */
export function normalizeThreshold(
  value: unknown,
  meta: DeliveryThresholdMeta,
  fallback: number
): number {
  const raw = typeof value === 'number' && Number.isFinite(value) ? value : fallback;
  return clampToBounds(quantize(raw, meta), meta);
}

/**
 * Setter-time ordering clamp for the value BEING WRITTEN. Returns what to
 * persist for `id` given the current sibling snapshot; the sibling entry is
 * read and never modified. Unpaired channels are returned normalized only.
 */
export function clampAgainstSibling(
  id: AlertChannelId,
  value: number,
  channels: Readonly<Record<AlertChannelId, AlertChannelSettings>>
): number {
  const meta = ALERT_REGISTRY[id].threshold;
  if (meta === null) return value;

  const pair = ORDERED_PAIRS.find((p) => p.lower === id || p.upper === id);
  if (!pair) return normalizeThreshold(value, meta, value);

  const siblingId = pair.lower === id ? pair.upper : pair.lower;
  const sibling = channels[siblingId]?.deliveryThreshold;
  if (typeof sibling !== 'number' || !Number.isFinite(sibling)) {
    return normalizeThreshold(value, meta, value);
  }

  // One step past the sibling, then re-clamped to this channel's own bounds.
  // At a bound the pair can collapse to equality — no room left to separate
  // them — which is the documented degenerate case, not a silent sibling edit.
  const bounded =
    pair.lower === id
      ? Math.min(value, sibling - meta.step)
      : Math.max(value, sibling + meta.step);
  return normalizeThreshold(bounded, meta, value);
}

/**
 * What the read path has built by the time it normalizes: sound/volume already
 * resolved, the two delivery fields still possibly absent (a v2 payload has
 * never heard of them).
 */
export type DeliveryChannelsInput = Record<
  AlertChannelId,
  Omit<AlertChannelSettings, 'visualEnabled' | 'deliveryThreshold'> &
    Partial<Pick<AlertChannelSettings, 'visualEnabled' | 'deliveryThreshold'>>
>;

/**
 * Read-time pass over all channels. Fills missing delivery fields from registry
 * defaults, clamps each channel against its OWN bounds, and forces the fields
 * off on channels whose registry meta does not declare the capability.
 *
 * Pure: does not mutate the input, does not write storage, does not look at a
 * sibling. Unknown per-channel keys are spread through (#231 losslessness).
 */
export function normalizeDeliveryChannels(
  channels: DeliveryChannelsInput
): Record<AlertChannelId, AlertChannelSettings> {
  const out = {} as Record<AlertChannelId, AlertChannelSettings>;
  for (const id of ALERT_CHANNEL_IDS) {
    const meta = ALERT_REGISTRY[id];
    const stored = channels[id];
    const visualEnabled = meta.visual
      ? typeof stored?.visualEnabled === 'boolean'
        ? stored.visualEnabled
        : meta.defaults.visualEnabled
      : false;
    const deliveryThreshold =
      meta.threshold === null
        ? null
        : normalizeThreshold(
            stored?.deliveryThreshold,
            meta.threshold,
            meta.defaults.deliveryThreshold ?? meta.threshold.min
          );
    out[id] = { ...stored, visualEnabled, deliveryThreshold };
  }
  return out;
}
